import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups, TableDropdown } from 'components/molecules';
import LabeledReportStatus from 'components/molecules/labeledStatus/LabeledReportStatus';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiNavigationArrow, PiXCircle } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelLabelPeriodReport,
  useCheckLabelReportPeriodsMutation,
  useGetLabelReportsPeriodOrders,
  usePostLabelPeriodReport,
  usePostPeriodReport,
  usePostPeriodReportForPeriod,
} from 'services/label-report';
import {
  ILabelReportMutationForm,
  IOrderAllPeriodReportForm,
  IPeriodReportForPeriodMutationForm,
  REPORT_GENERATION_STATUS,
  ILabelReportsOrderResponse,
} from 'services/label-report/types';
import { useGetLabelLightList } from 'services/labels/light';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { magellanColumns } from 'types/components/table/table.types';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { useSearchById, useSetSearchById } from 'store/state';
import { boolean, object, string } from 'yup';

enum LABELREPORTS_COLUMNS {
  label,
  startSettlementPeriod,
  endSettlementPeriod,
  plannedStartDate,
  status,
}

interface ILabelReportsSearch {
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
}

const labelReportsSearchInitialValues: ILabelReportsSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};

const labelRaportInitialValue: ILabelReportMutationForm = {
  labelId: '',
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  planedStartDate: formatDateToInput(new Date(), true),
  asap: true,
};

export const labelReportIndividualValidator = object().shape({
  startSettlementPeriodId: string().required(
    i18next.t('formik-error.required')
  ),
  endSettlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedStartDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean().required(i18next.t('formik-error.required')),
});

const labelReportValidator = labelReportIndividualValidator.shape({
  labelId: string().required(i18next.t('formik-error.required')),
});

const periodReportInitialValue: IOrderAllPeriodReportForm = {
  planedStartDate: formatDateToInput(new Date(), true),
  asap: true,
};

const periodReportValidator = object().shape({
  planedStartDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean().required(i18next.t('formik-error.required')),
});

const periodReportForPeriodInitialValue: IPeriodReportForPeriodMutationForm = {
  settlementPeriodId: '',
  planedStartDate: formatDateToInput(new Date(), true),
  asap: true,
  ignoreExistingReports: false,
};

const periodReportForPeriodValidator = periodReportValidator.shape({
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  ignoreExistingReports: boolean().required(i18next.t('formik-error.required')),
});

const LabelReports = () => {
  usePermissionGuard('LabelReportOrderPermissionsLIST**');
  const hasPermission = useHasPermission();
  const createLabelReport = usePostLabelPeriodReport();
  const getSettelmentPeriod = useGetSettlementLightList({ controlApplicationSettings: true });
  const getLabelLightList = useGetLabelLightList();
  
  const i18nextPrefix = 'label-reports.list';

  const columnsVisibility = useTableColumnsVisibility(
    'LabelReports',
    LABELREPORTS_COLUMNS
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || labelReportsSearchInitialValues;
  
  const getLabelReports = useGetLabelReportsPeriodOrders(
    { page: 0, itemCount: 25 },
    filters.startSettlementPeriodId || undefined,
    filters.endSettlementPeriodId || undefined
  );
  const createPeriodReport = usePostPeriodReport();
  const createPeriodReportForPeriod = usePostPeriodReportForPeriod();
  const cancelLabelReport = useCancelLabelPeriodReport();
  const checkPeriods = useCheckLabelReportPeriodsMutation();

  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningReason, setWarningReason] = useState<
    'overlapping' | 'alreadySettled' | 'sourceReportsPending' | null
  >(null);
  const [reportData, setReportData] = useState<{
    labelId: string;
    startSettlementPeriodId: string;
    endSettlementPeriodId: string;
    planedStartDate: string;
    asap: boolean;
  } | null>(null);

  const tableProps = useInfiniteTable(getLabelReports, { selectable: false });

  const { ref: filterRef, isComponentVisible: isFilterVisible, handleComponentVisible: handleFilterVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);
  
  const { ref: dropdownRef, isComponentVisible: isDropdownVisible, handleComponentVisible: handleDropdownVisible } =
    useComponentVisible<HTMLDivElement>(false);

  usePermissionGuard('LabelReportOrderPermissionsLIST**');

  const { t } = useTranslation();

  const getLabelOptions = () =>
    getLabelLightList.data &&
    getLabelLightList.data.map(label => ({
      value: label.id,
      label: label.labelName,
    }));

  const getSettlementPeriodOptions = () =>
    getSettelmentPeriod.data?.flat().map(settelment => ({
      value: settelment.id,
      label: getSettlementPeriodName(settelment.settlementPeriodName),
    }));

  const onSubmitByLabel = async (
    values: ILabelReportMutationForm,
    helpers: FormikHelpers<ILabelReportMutationForm>,
    actions: PopupActions | null
  ) => {
    try {
      const checkResult = await checkPeriods.mutateAsync({
        labelId: values.labelId,
        startSettlementPeriodId: values.startSettlementPeriodId,
        endSettlementPeriodId: values.endSettlementPeriodId,
      });

      if (checkResult.areAllSourceReportsDone === false) {
        setReportData({
          labelId: values.labelId,
          startSettlementPeriodId: values.startSettlementPeriodId,
          endSettlementPeriodId: values.endSettlementPeriodId,
          planedStartDate: values.planedStartDate,
          asap: values.asap,
        });
        setWarningReason('sourceReportsPending');
        setShowWarningPopup(true);
        actions?.close();
        return;
      }

      if (
        checkResult.hasOverlappingPeriods ||
        checkResult.isAlreadySettled
      ) {
        setReportData({
          labelId: values.labelId,
          startSettlementPeriodId: values.startSettlementPeriodId,
          endSettlementPeriodId: values.endSettlementPeriodId,
          planedStartDate: values.planedStartDate,
          asap: values.asap,
        });
        setWarningReason(
          checkResult.hasOverlappingPeriods ? 'overlapping' : 'alreadySettled',
        );
        setShowWarningPopup(true);
        actions?.close();
        return;
      }

      await createLabelReport.mutateAsync(
        {
          ...values,
          planedStartDate: values.planedStartDate.length
            ? new Date(values.planedStartDate).toISOString()
            : null,
        },
        {
          onSuccess: async () => {
            await getLabelReports.refetch();
            helpers.setValues(labelRaportInitialValue);
            actions?.close();
          },
        }
      );
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  const onSubmitAll = async (
    values: IOrderAllPeriodReportForm,
    helpers: FormikHelpers<IOrderAllPeriodReportForm>,
    actions: PopupActions | null
  ) => {
    try {
      await createPeriodReport.mutateAsync(
        {
          asap: values.asap,
          planedStartDate: values.planedStartDate.length
            ? new Date(values.planedStartDate).toISOString()
            : null,
        },
        {
          onSuccess: async () => {
            await getLabelReports.refetch();
            helpers.setValues(periodReportInitialValue);
            actions?.close();
          },
        }
      );
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  const onSubmitForPeriod = async (
    values: IPeriodReportForPeriodMutationForm,
    helpers: FormikHelpers<IPeriodReportForPeriodMutationForm>,
    actions: PopupActions | null
  ) => {
    await createPeriodReportForPeriod.mutateAsync(
      {
        ...values,
        planedStartDate: values.planedStartDate.length
          ? new Date(values.planedStartDate).toISOString()
          : null,
      },
      {
        onSuccess: async () => {
          await getLabelReports.refetch();
          helpers.setValues(periodReportForPeriodInitialValue);
          actions?.close();
        },
      }
    );
  };

  const handleCreateReport = async () => {
    if (reportData) {
      try {
        await createLabelReport.mutateAsync(
          {
            ...reportData,
            planedStartDate: reportData.planedStartDate.length
              ? new Date(reportData.planedStartDate).toISOString()
              : null,
          },
          {
            onSuccess: async () => {
              setShowWarningPopup(false);
              setWarningReason(null);
              await getLabelReports.refetch();
            },
          }
        );
      } catch (error) {
        if (error instanceof Error) {
          onErrorShowAllErrorsToast(error);
        } else {
          onErrorShowAllErrorsToast(
            new Error('Unknown error occurred while creating report')
          );
        }
      }
    }
  };

  const cancelLabelReportFuncion = async (
    labelReportOrderId: string,
    actions: PopupActions
  ) => {
    await cancelLabelReport.mutateAsync(
      { labelReportOrderId },
      {
        onSuccess: async () => {
          await getLabelReports.refetch();
          actions.close();
        },
        onError: async error => {
          onErrorShowAllErrorsToast(error);
          await getLabelReports.refetch();
        },
      }
    );
  };

  const goToLabel = (id: string) => window.open(`/label/${id}/reports`, '_blank');
  const buttonStyle = { justifyContent: 'start' };


  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex gap-2'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleFilterVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={LABELREPORTS_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
                {hasPermission('LabelReportOrderPermissionsLIST**', 1) && (
                  <Button
                    variant={BUTTON_VARIANT.submit}
                    onClick={handleDropdownVisible}
                    outsideChildren={
                      <DropdownMenu
                        ref={dropdownRef}
                        visible={isDropdownVisible}
                        style={{ padding: 5 }}
                        justifyPosition={'end'}>
                        <div className='flex flex-col w-full'>
                          <MagellanPopups.Form
                            initialValues={labelRaportInitialValue}
                            validationSchema={labelReportValidator}
                            onSubmit={onSubmitByLabel}
                            header={t('label-reports.list.popup.header')}
                            trigger={
                              <Button
                                isClear
                                buttonStyle={buttonStyle}
                                fullWidth>
                                <Button.Text
                                  text={t(`label-reports.create.orderByLabel`)}
                                />
                              </Button>
                            }>
                            {({ values }) => (
                              <>
                                <FormInput.Select
                                  label={t('input.label.label')}
                                  name='labelId'
                                  isAsync
                                  options={getLabelOptions()}
                                />
                                <FormInput.Select
                                  label={t('input.label.startSettlementPeriod')}
                                  name='startSettlementPeriodId'
                                  isAsync
                                  options={getSettlementPeriodOptions()}
                                />
                                <FormInput.Select
                                  label={t('input.label.endSettlementPeriod')}
                                  name='endSettlementPeriodId'
                                  isAsync
                                  options={getSettlementPeriodOptions()}
                                />
                                <FormInput.Select
                                  name={`asap`}
                                  label={t('input.label.asap')}
                                  options={[
                                    {
                                      label: t('input.option.yes'),
                                      value: true,
                                    },
                                    {
                                      label: t('input.option.no'),
                                      value: false,
                                    },
                                  ]}
                                />
                                <FormInput.Text
                                  label={t('input.label.plannedStartDate')}
                                  type='datetime-local'
                                  min={formatDateToInput(new Date(), true)}
                                  name='planedStartDate'
                                  disabled={values.asap !== false}
                                />
                              </>
                            )}
                          </MagellanPopups.Form>
                          <MagellanPopups.Form
                            header={t('period-reports.popup.header')}
                            trigger={
                              <Button
                                isClear
                                buttonStyle={buttonStyle}
                                fullWidth>
                                <Button.Text
                                  text={t(`label-reports.create.orderAll`)}
                                />
                              </Button>
                            }
                            initialValues={periodReportInitialValue}
                            validationSchema={periodReportValidator}
                            onSubmit={onSubmitAll}>
                            {({ values }) => (
                              <>
                                <p className='mb-4 text-center max-w-80'>
                                  {t('label-reports.create.orderAllDescription')}
                                </p>
                                <FormInput.Select
                                  name={`asap`}
                                  label={t('input.label.asap')}
                                  options={[
                                    {
                                      label: t('input.option.yes'),
                                      value: true,
                                    },
                                    {
                                      label: t('input.option.no'),
                                      value: false,
                                    },
                                  ]}
                                />
                                <FormInput.Text
                                  label={t('input.label.plannedStartDate')}
                                  type='datetime-local'
                                  min={formatDateToInput(new Date(), true)}
                                  name='planedStartDate'
                                  disabled={values.asap !== false}
                                />
                              </>
                            )}
                          </MagellanPopups.Form>
                          {hasPermission('ServicePermissionsLIST**', 1) && (
                            <MagellanPopups.Form
                              header={t('label-reports.create.headerForPeriod')}
                              trigger={
                                <Button
                                  isClear
                                  buttonStyle={buttonStyle}
                                  fullWidth>
                                  <Button.Text
                                    text={t(
                                      'label-reports.create.orderForPeriod'
                                    )}
                                  />
                                </Button>
                              }
                              initialValues={periodReportForPeriodInitialValue}
                              validationSchema={periodReportForPeriodValidator}
                              onSubmit={onSubmitForPeriod}>
                              {({ values }) => (
                                <>
                                  <FormInput.Select
                                    label={t('input.label.settlementPeriod')}
                                    name='settlementPeriodId'
                                    options={getSettlementPeriodOptions()}
                                    isAsync
                                  />
                                  <FormInput.Select
                                    name='asap'
                                    label={t('input.label.asap')}
                                    options={[
                                      {
                                        label: t('input.option.yes'),
                                        value: true,
                                      },
                                      {
                                        label: t('input.option.no'),
                                        value: false,
                                      },
                                    ]}
                                  />
                                  <FormInput.Text
                                    label={t('input.label.plannedStartDate')}
                                    type='datetime-local'
                                    min={formatDateToInput(new Date(), true)}
                                    name='planedStartDate'
                                    disabled={values.asap !== false}
                                  />
                                  <FormInput.Checkbox
                                    name='ignoreExistingReports'
                                    label={t(
                                      'input.label.ignoreExistingReports'
                                    )}
                                  />
                                </>
                              )}
                            </MagellanPopups.Form>
                          )}
                        </div>
                      </DropdownMenu>
                    }>
                    <Button.Text text={t(`label-reports.addOrder`)} />
                  </Button>
                )}
              </div>
            </>
          }>
          <QueryFilter<ILabelReportsSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<ILabelReportsSearch>
                ref={filterRef}
                isOpen={isFilterVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startSettlementPeriod')}
                    options={getSettlementPeriodOptions()}
                    isAsync
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    label={t('input.label.endSettlementPeriod')}
                    options={getSettlementPeriodOptions()}
                    isAsync
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isFilterVisible}
                customLabel={data => {
                  const period = getSettelmentPeriod.data?.flat().find(p => p.id === data.value);
                  return period ? getSettlementPeriodName(period.settlementPeriodName) : `${data.value}`;
                }}
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        size='compact'
        id={columnsVisibility.tableId}
        {...tableProps}
        columns={magellanColumns<ILabelReportsOrderResponse>([
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            minWidth: 200,
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.label),
          },
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriod }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(
                    startSettlementPeriod.settlementPeriodName
                  )}
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    startSettlementPeriod.startDate &&
                    formatDate(startSettlementPeriod.startDate)
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              LABELREPORTS_COLUMNS.startSettlementPeriod
            ),
          },

          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriod }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(
                    endSettlementPeriod.settlementPeriodName
                  )}
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    endSettlementPeriod.endDate &&
                    formatDate(endSettlementPeriod.endDate)
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              LABELREPORTS_COLUMNS.endSettlementPeriod
            ),
          },
          {
            label: t('input.label.plannedStartDate'),
            renderCell: ({ plannedStartDate }) => (
              <TableItem>
                <TableItem.Text text={formatDate(plannedStartDate, true)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.plannedStartDate),
          },
          {
            label: t('input.label.status'),
            renderCell: ({ reportGenerationStatusId }) => (
              <TableItem>
                <LabeledReportStatus status={reportGenerationStatusId} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.status),
            minWidth: 140,
          },
          {
            label: '',
            minWidth: 56,
            renderCell: ({ id, reportGenerationStatusId, labelId }) => (
              <TableItem justifyPosition='end'>
                <TableDropdown justifyPosition='end' style={{ padding: 0 }}>
                  {reportGenerationStatusId ===
                    REPORT_GENERATION_STATUS.Waiting && (
                    <MagellanPopups.CancelWarning
                      name={t('label-reports.what')}
                      onCancel={actions => cancelLabelReportFuncion(id, actions)}
                      trigger={
                        <Button
                          type='button'
                          isClear
                          fullWidth
                          size='compact'
                          buttonStyle={{ justifyContent: 'start' }}>
                          <Button.Icon icon={<PiXCircle />} />
                          <Button.Text text={t('button.cancel')} />
                        </Button>
                      }
                    />
                  )}
                  <Button
                    type='button'
                    isClear
                    fullWidth
                    size='compact'
                    onClick={() => goToLabel(labelId)}
                    buttonStyle={{ justifyContent: 'start' }}>
                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                    <Button.Text text={t('button.open')} />
                  </Button>
                </TableDropdown>
              </TableItem>
            ),
          },
        ])}
      />
      <MagellanPopups.CriticalWarningPopup
        open={showWarningPopup}
        onClose={() => {
          setShowWarningPopup(false);
          setWarningReason(null);
        }}
        onConfirm={
          warningReason === 'sourceReportsPending'
            ? undefined
            : handleCreateReport
        }
        title={t('label-reports.warning.title')}
        message={
          warningReason === 'sourceReportsPending'
            ? t('label-reports.warning.sourceReportsPendingMessage')
            : warningReason === 'alreadySettled'
              ? t('label-reports.warning.alreadySettledMessage')
              : t('label-reports.warning.message')
        }
        confirmButtonText={t('popup.generate')}
        cancelButtonText={t('popup.resign')}
      />
    </TopbarPage>
  );
};

export default LabelReports;
